import {
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpRequest,
  HttpHandlerFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, filter, take, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { TranslationService } from '../services/i18n/translation.service';
import { environment } from '../../../environments/environment';

/**
 * Add authorization header to request
 */
const addTokenToRequest = (req: HttpRequest<unknown>, token: string): HttpRequest<unknown> => {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * HTTP Interceptor to add JWT authentication token to all API requests
 * and handle silent token refresh on 401 errors
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const token = authService.getAccessToken();

  // The API answers its messages in the user's language; other hosts (presigned files) are left alone
  if (req.url.startsWith(environment.apiBaseUrl)) {
    const language = inject(TranslationService).getCurrentLanguage()();
    req = req.clone({ setHeaders: { 'Accept-Language': language } });
  }

  // Skip adding token for authentication endpoints (login, register, and token refresh)
  if (
    req.url.includes('/auth/login') ||
    req.url.includes('/auth/register') ||
    req.url.includes('/token/') ||
    req.url.includes('/token/refresh')
  ) {
    return next(req);
  }

  // Clone request and add authorization header if token exists
  if (token) {
    const authReq = addTokenToRequest(req, token);

    return next(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // Only attempt refresh on 401 Unauthorized errors
        if (error.status === 401) {
          return handleTokenRefresh(authService, req, next, error);
        }
        return throwError(() => error);
      })
    );
  }

  return next(req);
};

/**
 * Handle token refresh logic
 * If refresh is already in progress, wait for it to complete
 * Otherwise, initiate a new refresh
 */
const handleTokenRefresh = (
  authService: AuthService,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  error: HttpErrorResponse
) => {
  if (authService.getIsRefreshing()) {
    // Wait for the ongoing refresh to finish; if it failed, fail with the original 401
    return authService.getRefreshTokenSubject().pipe(
      filter(() => !authService.getIsRefreshing()),
      take(1),
      switchMap((newToken) =>
        newToken ? next(addTokenToRequest(req, newToken)) : throwError(() => error)
      )
    );
  }

  // Initiate token refresh
  return authService.refreshAccessToken().pipe(
    switchMap((newToken) => {
      return next(addTokenToRequest(req, newToken));
    }),
    catchError((refreshError) => {
      // authService.refreshAccessToken() logs out when the refresh token itself is refused
      return throwError(() => refreshError);
    })
  );
};
